import { type AxiosError, type AxiosResponse, isAxiosError } from 'axios';
import { redirect } from 'react-router';
import { getSearchResponse } from '~/api';
import { ApiClient } from '~/api/Endpoints';
import { getFiltersFromQueryParam } from '~/hooks/useFiltersFromQueryParam';
import { deserializeAxiosError, serializeAxiosError } from '~/lib/axios';
import type { InitialData } from '~/lib/getInitialData';
import { rootDataContext } from '~/lib/rootDataContext';
import type { LoaderDataResponse } from '~/scenes/reports/types';
import { getSortFromQueryParam } from '~/scenes/reports/useSortFromQueryParam';
import getLogoFromSearch from '~/scripts/getLogoFromSearch';
import { getNameFromSearch } from '~/scripts/getNameFromSearch';
import isClient from '~/scripts/isClient';
import { isEmptySearch } from '~/scripts/isEmptySearch';
import isServer from '~/scripts/isServer';
import { isSingleReportSearch } from '~/scripts/isSingleReportSearch';
import parseQueryFromSearchParams, {
  getRawUrlParameterFromUrl,
} from '~/scripts/parseQueryFromSearchParams';
import type {
  Breadcrumb,
  Reports,
  Search,
  Searches,
  Suggestions,
} from '~/types';
import type { Route } from '../../routes/+types/reports_.$name.($uuid)';
import { SESSION_HASH_EXPIRED_ERROR_CODE } from '@shared/constants';
import { cleanReportNameFromUrl } from '@shared/utilities/cleanReportName';

const getAsyncData = async (
  request: Route.LoaderArgs['request'],
  params: Route.LoaderArgs['params'],
  rootData: InitialData
): Promise<LoaderDataResponse> => {
  const api = new ApiClient(request);
  const cached_only = isServer() || undefined;
  const requestUrl = new URL(request.url);
  const searchParams = parseQueryFromSearchParams(requestUrl.searchParams);
  const breadcrumbs: Breadcrumb[] = [
    {
      text: 'Home',
      href: `${import.meta.env.VITE_ORIGIN}/`,
    },
    {
      text: 'All Technologies',
      href: `${import.meta.env.VITE_ORIGIN}/technologies`,
    },
  ];

  try {
    const uuid = (params.uuid || '').toString();
    if (!uuid) {
      const searchJson = getSearchJson(request);
      // no ?search= param, check for legacy ?query= param
      if (isEmptySearch(searchJson)) {
        if (typeof searchParams.query === 'string') {
          return redirect(
            `/reports/new?search=${JSON.stringify({
              all: [{ type: 'code', value: searchParams.query }],
              any: [],
              none: [],
            })}`
          );
        }

        // redirect to report if exists
        if (params.name !== 'new') {
          const [suggestion] = await api.get('/suggestions', {
            query: {
              search: cleanReportNameFromUrl(params.name.toString()),
            },
          });
          if (suggestion?.uuid) {
            return redirect(
              `/reports/new?search=${JSON.stringify({
                all: [{ type: 'report', value: suggestion.uuid }],
                any: [],
                none: [],
              })}`,
              301
            );
          }
        }

        // return /reports/new metadata
        return {
          breadcrumbs: [
            ...breadcrumbs,
            {
              text: 'New Report',
              href: `${import.meta.env.VITE_ORIGIN}/reports/new`,
              disabled: true,
            },
          ],
        };
      }
      let { url } = await putSearch(
        request,
        searchJson,
        isClient() ? window.abortController?.signal : undefined
      );
      const filters = getRawUrlParameterFromUrl(request.url, 'filters');
      const sort = getRawUrlParameterFromUrl(request.url, 'sort');

      url += '?redirected=true';
      if (filters) {
        url += `&${filters}`;
      }
      if (sort) {
        url += `&${sort}`;
      }
      return redirect(url, 301);
    }
    const searchesRequest = api.get('/searches/:uuid', {
      params: { uuid },
      signal: isClient() ? window.abortController?.signal : undefined,
    });

    const searchesResponse = await searchesRequest;

    if (requestUrl.pathname !== searchesResponse.url) {
      const destination = `${requestUrl.origin}${searchesResponse.url}${requestUrl.search}`;
      return redirect(destination, 301);
    }
    // qs.parse(requestUrl.search.substring(1), {
    //   decoder: (str: string) => {
    //     // whatever the bug is, it's probably here
    //     return decodeURIComponent(str);
    //   },
    // });
    const filters = getFiltersFromQueryParam(searchParams.filters || {});
    const sortParam = (searchParams.sort || '').toString();
    const [sort] = sortParam ? getSortFromQueryParam(sortParam) : [undefined];
    const start = searchParams.start ? +searchParams.start : undefined;
    const logo = getLogoFromSearch(searchesResponse.search);
    let searchResponse: AxiosResponse<Search.GetSearchResponse> | undefined;
    let error: Error | AxiosError | undefined;
    let reportDetails: Reports.ReportDetails | undefined;
    let history: Reports.ReportHistory[] | undefined;
    let report: Suggestions.Suggestion | undefined;
    const name = getNameFromSearch(searchesResponse.search, []);
    breadcrumbs.push(
      ...[
        {
          text: `Websites using ${name}`,
          href: `${import.meta.env.VITE_ORIGIN}${searchesResponse.url}`,
          disabled: true,
        },
      ]
    );
    const hasFilters = Object.keys(filters).length > 0;
    // facets should reflect the search itself, not the applied filters,
    // so fetch them from the unfiltered search when filters are present
    const unfilteredFacetsRequest: Promise<Search.Facets | undefined> =
      hasFilters
        ? getSearchResponse(
            api,
            {
              search: searchesResponse.search,
              cached_only,
              authorized: rootData?.user.isGuest ? undefined : true,
            },
            isClient() ? window.abortController?.signal : undefined
          )
            .then((response) => {
              return response.data.facets;
            })
            .catch(() => {
              return undefined;
            })
        : Promise.resolve(undefined);
    try {
      searchResponse = await getSearchResponse(
        api,
        {
          search: searchesResponse.search,
          filters,
          sort,
          start,
          cached_only,
          authorized: rootData?.user.isGuest ? undefined : true,
        },
        isClient() ? window.abortController?.signal : undefined
      );
    } catch (e) {
      const serializedError = serializeAxiosError(e as Error | AxiosError);
      const deserializedError = deserializeAxiosError(serializedError);

      // errors that we can ignore and proceed to clientLoader
      if (isAxiosError(deserializedError)) {
        if (
          deserializedError.code === 'ERR_CANCELED' ||
          deserializedError.name === 'AbortError' ||
          (deserializedError.code === SESSION_HASH_EXPIRED_ERROR_CODE &&
            isServer())
        ) {
          return {
            isLoading: true,
            total: undefined,
            ...searchesResponse,
            reportDetails,
            report,
            error: undefined,
            breadcrumbs,
          };
        }
      }
      if (deserializedError?.message === 'CACHE_MISS') {
        return {
          isLoading: true,
          total: undefined,
          ...searchesResponse,
          reportDetails,
          report,
          error: undefined,
          breadcrumbs,
        };
      }
      return {
        error: serializeAxiosError(e as Error | AxiosError),
      };
    }

    if (isSingleReportSearch(searchesResponse.search)) {
      const [reportDetailsRes, suggestionsRes, historyRes] = await Promise.all([
        api.get('/reports/:uuid', {
          params: {
            uuid: searchesResponse.search.all[0]!.value,
          },
        }),
        api.get('/suggestions', {
          query: {
            search: searchesResponse.search.all[0]!.value,
          },
        }),
        api.get('/reports/:uuid/history', {
          params: {
            uuid: searchesResponse.search.all[0]!.value,
          },
        }),
      ]);
      reportDetails = reportDetailsRes;
      report = suggestionsRes[0];
      history = historyRes.history;
    }

    const searchFacets = hasFilters
      ? await unfilteredFacetsRequest
      : searchResponse?.data.facets;

    return {
      ...searchResponse?.data,
      ...searchesResponse,
      searchFacets,
      logo,
      reportDetails,
      report,
      history,
      retryAfter: searchResponse?.headers['retry-after'],
      error,
      breadcrumbs,
    };
  } catch (error) {
    return {
      error: serializeAxiosError(error as Error | AxiosError),
    };
  }
};

let hydrated = false;

export const clientLoader = async ({
  request,
  serverLoader,
  params,
  context,
}: Route.ClientLoaderArgs) => {
  if (!hydrated) {
    hydrated = true;
    const serverData = await serverLoader();
    const hasValidServerData =
      !serverData.isLoading &&
      serverData.total !== undefined &&
      serverData.total >= 0;
    // @ts-expect-error used for cypress
    window.__reports_server_hydrated = !!hasValidServerData;
    if (hasValidServerData) {
      return serverData;
    }
  }

  return getAsyncData(request, params, context.get(rootDataContext)!);
};
clientLoader.hydrate = true as const;

export const loader = async ({
  request,
  params,
  context,
}: Route.LoaderArgs): Promise<LoaderDataResponse> => {
  const rootData = context.get(rootDataContext);
  return getAsyncData(request, params, rootData!);
};

const getSearchJson = (request: Route.LoaderArgs['request']) => {
  const empty: Searches.StoredSearchObject = { all: [], any: [], none: [] };
  try {
    const searchString = new URL(request.url).searchParams.get('search');
    const searchJson: Searches.StoredSearchObject = JSON.parse(
      searchString || JSON.stringify(empty)
    );
    if (isEmptySearch(searchJson)) {
      return empty;
    }
    return searchJson;
  } catch {
    return empty;
  }
};

const putSearch = async (
  request: Route.LoaderArgs['request'],
  search: Searches.StoredSearchObject,
  signal: AbortSignal | undefined
) => {
  const api = new ApiClient(request);
  return api.put('/searches', {
    body: { search },
    signal,
  });
};
